import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

/**
 * Hooks for the phone desk (/desk) — the narrow login the live receptionists
 * use to help a caller. Everything flows through the desk-* functions, which
 * check authedPhoneDesk() server-side: a receptionist holds no customer
 * membership, so nothing here reads RLS tables directly (except useDeskAccess,
 * which reads the caller's own customer_users row).
 *
 * Orders go through submit-order — the same pipeline as every portal order —
 * with `phone_desk: true`, which tags them as placed by the phone desk.
 */

export interface DeskAccess {
  isPhoneDesk: boolean;
  isSuperadmin: boolean;
  /** May use /desk at all. */
  allowed: boolean;
  name: string | null;
}

/** Whether the signed-in user may use the phone desk (phone desk OR superadmin). */
export function useDeskAccess(): { access: DeskAccess | null; loading: boolean } {
  const { data, isLoading } = useQuery<DeskAccess>({
    queryKey: ['desk-access'],
    queryFn: async () => {
      const none: DeskAccess = { isPhoneDesk: false, isSuperadmin: false, allowed: false, name: null };
      const { data: authData } = await supabase.auth.getUser();
      const userId = authData.user?.id;
      if (!userId) return none;
      const { data: row, error } = await supabase
        .schema('orders')
        .from('customer_users')
        .select('is_phone_desk, is_superadmin, full_name')
        .eq('id', userId)
        .maybeSingle();
      if (error || !row) return none;
      const isPhoneDesk = row.is_phone_desk === true;
      const isSuperadmin = row.is_superadmin === true;
      const meta = authData.user?.user_metadata as { full_name?: string; name?: string } | undefined;
      return {
        isPhoneDesk,
        isSuperadmin,
        allowed: isPhoneDesk || isSuperadmin,
        name: row.full_name ?? meta?.full_name ?? meta?.name ?? authData.user?.email ?? null,
      };
    },
    staleTime: 5 * 60 * 1000,
  });
  return { access: data ?? null, loading: isLoading };
}

async function deskFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error('not signed in');
  const res = await fetch(`/.netlify/functions/${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...(init.headers ?? {}) },
  });
  const text = await res.text();
  let body: (T & { ok?: boolean; error?: string; code?: string }) | null = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    /* fall through */
  }
  if (!res.ok || !body || body.ok === false) {
    const err = new Error(body?.error ?? `HTTP ${res.status}`) as Error & { code?: string };
    err.code = body?.code;
    throw err;
  }
  return body;
}

export interface DeskSearchResult {
  id: string;
  name: string;
  qbo_customer_id: number | null;
  account_number: string | null;
  store_code: string | null;
  city: string | null;
  location_count: number;
  matched: string | null;
  location_id: string | null;
  ordering_paused: boolean;
}

export function useDeskSearch(q: string) {
  const term = q.trim();
  return useQuery<DeskSearchResult[]>({
    queryKey: ['desk-search', term],
    enabled: term.length >= 2,
    queryFn: async () =>
      (await deskFetch<{ results: DeskSearchResult[] }>(`desk-search?q=${encodeURIComponent(term)}`)).results,
    staleTime: 30 * 1000,
    placeholderData: (prev) => prev,
  });
}

export interface DeskLocation {
  id: string;
  name: string;
  address: string | null;
  city_line: string | null;
  phone: string | null;
  delivery_notes: string | null;
  delivery_days: string | null;
  cutoff: string | null;
  next_delivery_date: string | null;
}
export interface DeskContact {
  name: string;
  role: string | null;
  phone: string | null;
  email: string | null;
}
export interface DeskOrder {
  id: string;
  order_number: string | null;
  location: string | null;
  status: string;
  sf_job_status: string | null;
  scheduled_date: string | null;
  requested_date: string | null;
  submitted_at: string | null;
  delivered_at: string | null;
  fulfillment: string | null;
  po_number: string | null;
  placed_via: string;
  open: boolean;
  lines: Array<{ catalog_item_id: string | null; name: string; quantity: number }>;
}
export interface DeskServiceRequest {
  id: string;
  kind: string;
  subject: string;
  status: string;
  created_at: string;
  sf_job_id: string | null;
  location: string | null;
}
export interface DeskCustomer {
  customer: {
    id: string;
    name: string;
    qbo_customer_id: number | null;
    account_number: string | null;
    store_code: string | null;
    ordering_paused: boolean;
  };
  locations: DeskLocation[];
  contacts: DeskContact[];
  orders: DeskOrder[];
  service_requests: DeskServiceRequest[];
}

export function useDeskCustomer(customerId: string | null) {
  return useQuery<DeskCustomer>({
    queryKey: ['desk-customer', customerId],
    enabled: !!customerId,
    queryFn: () => deskFetch<DeskCustomer>(`desk-customer?customer_id=${encodeURIComponent(customerId!)}`),
    staleTime: 30 * 1000,
  });
}

export interface DeskItem {
  id: string;
  name: string;
  sku: string | null;
  category: string | null;
  pack_size: string | null;
  image: string | null;
  accent: string | null;
  price: number;
}

export function useDeskCatalog(customerId: string | null, enabled: boolean) {
  return useQuery<{ items: DeskItem[]; usual: string[] }>({
    queryKey: ['desk-catalog', customerId],
    enabled: !!customerId && enabled,
    queryFn: () => deskFetch<{ items: DeskItem[]; usual: string[] }>(`desk-catalog?customer_id=${encodeURIComponent(customerId!)}`),
    staleTime: 5 * 60 * 1000,
  });
}

export interface DeskOrderInput {
  customer_id: string;
  customer_location_id: string;
  lines: Array<{ catalog_item_id: string; quantity: number }>;
  po_number?: string;
  delivery_notes?: string;
  requested_delivery_date?: string;
  idempotency_key?: string;
  dry_run: boolean;
}
export interface DeskOrderPreview {
  order_lines: Array<{ catalog_item_id: string; display_name: string; sku: string | null; quantity: number; unit_price: number; line_total: number }>;
  fee_lines: Array<{ name: string; quantity: number; amount: number }>;
  cylinder_warnings?: Array<{ label: string; picking_up: number; on_hand: number }>;
  totals: { subtotal: number; fee_total: number; estimated_total: number };
}
export interface DeskOrderPlaced {
  order_id: string;
  order_number: string;
  sf_job_number?: string | number | null;
  email_sent?: boolean;
  idempotent_replay?: boolean;
  totals?: { estimated_total: number };
}

/** Preview (dry_run) or place an order through submit-order, tagged as the phone desk. */
export function useDeskSubmitOrder() {
  const qc = useQueryClient();
  return useMutation<DeskOrderPreview & DeskOrderPlaced, Error & { code?: string }, DeskOrderInput>({
    mutationFn: (input) =>
      deskFetch('submit-order', {
        method: 'POST',
        body: JSON.stringify({ ...input, fulfillment: 'delivery', phone_desk: true }),
      }),
    // A placed order shows up under "Open orders" straight away.
    onSuccess: (_res, input) => {
      if (!input.dry_run) void qc.invalidateQueries({ queryKey: ['desk-customer', input.customer_id] });
    },
  });
}

export interface DeskServiceInput {
  customer_id: string;
  location_id?: string;
  kind: 'equipment_issue' | 'refill' | 'scheduling' | 'other';
  subject: string;
  details?: string;
  caller_name?: string;
  callback_phone?: string;
}

export function useDeskServiceRequest() {
  const qc = useQueryClient();
  return useMutation<{ request_id: string; sf_job_id: string | null }, Error, DeskServiceInput>({
    mutationFn: (input) =>
      deskFetch('desk-service-request', { method: 'POST', body: JSON.stringify(input) }),
    onSuccess: (_res, input) => void qc.invalidateQueries({ queryKey: ['desk-customer', input.customer_id] }),
  });
}

/** Stable short hash for idempotency keys (not security-sensitive). */
export function stableHash(s: string): string {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0).toString(36);
}
